import { useEffect, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { postePunktesystemAufBoard } from './boardMeldung';

// Punktesystem der Saison. Feldnamen unverändert (docs/DATENMODELL.md):
// basis, ot, heimAuswaerts, perfekteWoche, playoffMultiplikator, komplettFalsch.
// Speichern schreibt punkteSystem per merge und postet eine Board-Meldung.

const STANDARD = {
  basis: { exakterTipp: 5, richtigeTendenz: 3, richtigerSieger: 1, zusatzfrage: 2 },
  ot: { aktiv: false, otRichtig: 2, otFalsch: -1, unentschiedenNachOT: 3 },
  heimAuswaerts: { aktiv: true, heimsiegExakt: 1, heimsiegTendenz: 1, heimsiegRichtig: 1, auswaertssiegExakt: 2, auswaertssiegTendenz: 1, auswaertssiegRichtig: 1 },
  perfekteWoche: { aktiv: true, bonus: 10 },
  playoffMultiplikator: { aktiv: true, wildCard: 1.5, divisional: 2.0, conference: 2.5, superBowl: 3.0 },
  komplettFalsch: { aktiv: false, punkteProSpiel: 1 }
};

const zahl = (v) => (v === '' ? '' : parseFloat(v));

function Zeile({ label, hinweis, wert, onChange, disabled, min = 0, step = 0.5, einheit }) {
  return (
    <div className={`zeile${disabled ? ' zeile--aus' : ''}`}>
      <div className="zeile__text">
        <div className="zeile__label">{label}</div>
        {hinweis && <div className="zeile__hinweis">{hinweis}</div>}
      </div>
      <div className="zeile__eingabe">
        <input type="number" className="zahl-input" value={wert ?? ''} min={min} step={step} disabled={disabled}
          onChange={e => onChange(zahl(e.target.value))} />
        {einheit && <span>{einheit}</span>}
      </div>
    </div>
  );
}

function Schalter({ an, onToggle }) {
  return (
    <button type="button" className={`schalter-button${an ? ' ist-an' : ''}`} onClick={onToggle} aria-pressed={an}>
      {an ? 'AN' : 'AUS'}
    </button>
  );
}

function Punktesystem({ saisonId, saisonName, config, neuLaden, zeigeToast }) {
  const { userProfile } = useAuth();
  const [ps, setPs] = useState(STANDARD);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    setPs(config?.punkteSystem ? { ...STANDARD, ...config.punkteSystem } : STANDARD);
  }, [config]);

  const geaendert = JSON.stringify(ps) !== JSON.stringify(config?.punkteSystem ? { ...STANDARD, ...config.punkteSystem } : STANDARD);
  const set = (gruppe, feld, wert) => setPs(p => ({ ...p, [gruppe]: { ...p[gruppe], [feld]: wert } }));

  const speichern = async () => {
    setLaeuft(true);
    try {
      await setDoc(doc(db, 'saisonKonfiguration', saisonId), {
        saison: saisonName,
        punkteSystem: ps,
        aufgaben: { punktesystemPruefen: false },
        letzteAenderung: new Date().toISOString(),
        geaendertVon: userProfile?.displayName || 'Admin'
      }, { merge: true });
      try {
        await postePunktesystemAufBoard(userProfile, saisonName, ps, config?.bonusfragen);
      } catch (e) {
        console.error('Board-Meldung fehlgeschlagen:', e);
      }
      await neuLaden();
      zeigeToast('Punktesystem gespeichert und im Board angekündigt');
    } catch (error) {
      zeigeToast('Fehler: ' + error.message, true);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <div className="karte">
        <h2>🎯 Basis</h2>
        <p className="karte__hinweis">Punkte je Tipp-Kategorie. Immer aktiv.</p>
        <Zeile label="Exakt" hinweis="Beide Scores stimmen" wert={ps.basis.exakterTipp} onChange={v => set('basis', 'exakterTipp', v)} />
        <Zeile label="Tendenz" hinweis="Sieger und Differenz stimmen" wert={ps.basis.richtigeTendenz} onChange={v => set('basis', 'richtigeTendenz', v)} />
        <Zeile label="Sieger" hinweis="Nur der Sieger stimmt" wert={ps.basis.richtigerSieger} onChange={v => set('basis', 'richtigerSieger', v)} />
        <Zeile label="Zusatzfrage" hinweis="Derzeit ohne Funktion, bleibt gespeichert" wert={ps.basis.zusatzfrage} onChange={v => set('basis', 'zusatzfrage', v)} />
      </div>

      <div className="karte">
        <div className="karte__kopfzeile">
          <h2>🏟️ Heim-/Auswärts-Bonus</h2>
          <Schalter an={ps.heimAuswaerts.aktiv} onToggle={() => set('heimAuswaerts', 'aktiv', !ps.heimAuswaerts.aktiv)} />
        </div>
        <p className="karte__hinweis">Zusatzpunkte je nachdem, ob das Heim- oder das Auswärtsteam gewonnen hat.</p>
        {[['heimsiegExakt', 'Heimsieg · exakt'], ['heimsiegTendenz', 'Heimsieg · Tendenz'], ['heimsiegRichtig', 'Heimsieg · Sieger'],
          ['auswaertssiegExakt', 'Auswärtssieg · exakt'], ['auswaertssiegTendenz', 'Auswärtssieg · Tendenz'], ['auswaertssiegRichtig', 'Auswärtssieg · Sieger']].map(([k, l]) => (
          <Zeile key={k} label={l} wert={ps.heimAuswaerts[k]} disabled={!ps.heimAuswaerts.aktiv} onChange={v => set('heimAuswaerts', k, v)} />
        ))}
      </div>

      <div className="karte">
        <div className="karte__kopfzeile">
          <h2>🌟 Perfekte Woche</h2>
          <Schalter an={ps.perfekteWoche.aktiv} onToggle={() => set('perfekteWoche', 'aktiv', !ps.perfekteWoche.aktiv)} />
        </div>
        <Zeile label="Bonus" hinweis="Wenn alle Spiele der Woche mindestens den Sieger treffen" wert={ps.perfekteWoche.bonus} disabled={!ps.perfekteWoche.aktiv} onChange={v => set('perfekteWoche', 'bonus', v)} />
      </div>

      <div className="karte">
        <div className="karte__kopfzeile">
          <h2>💔 Trostpunkte</h2>
          <Schalter an={ps.komplettFalsch?.aktiv === true} onToggle={() => set('komplettFalsch', 'aktiv', !ps.komplettFalsch?.aktiv)} />
        </div>
        <Zeile label="Punkte je Spiel" hinweis="Wenn alle getippten Spiele einer Woche daneben liegen" wert={ps.komplettFalsch?.punkteProSpiel ?? 1} step={0.1} disabled={!ps.komplettFalsch?.aktiv} onChange={v => set('komplettFalsch', 'punkteProSpiel', v)} />
      </div>

      <div className="karte">
        <div className="karte__kopfzeile">
          <h2>🏆 Playoff-Multiplikator</h2>
          <Schalter an={ps.playoffMultiplikator.aktiv} onToggle={() => set('playoffMultiplikator', 'aktiv', !ps.playoffMultiplikator.aktiv)} />
        </div>
        <p className="karte__hinweis">Alle Punkte eines Playoff-Spiels werden mit dem Faktor der Runde multipliziert.</p>
        {[['wildCard', 'Wild Card'], ['divisional', 'Divisional'], ['conference', 'Conference Championship'], ['superBowl', 'Super Bowl']].map(([k, l]) => (
          <Zeile key={k} label={l} wert={ps.playoffMultiplikator[k]} min={1} step={0.1} einheit="×" disabled={!ps.playoffMultiplikator.aktiv} onChange={v => set('playoffMultiplikator', k, v)} />
        ))}
      </div>

      <div className="karte karte--gelb">
        <p className="karte__hinweis">
          Speichern gilt sofort für die nächste Punkteberechnung und postet eine Meldung ins Board. Während der laufenden Saison nur bei Fehlern ändern.
        </p>
        <button type="button" className="aktion" onClick={speichern} disabled={laeuft || !geaendert}>
          {laeuft ? '⏳ Speichere…' : '💾 Punktesystem speichern'}
        </button>
      </div>
    </>
  );
}

export default Punktesystem;
